import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';
import { FONTS } from '../constants/config';
import { PendingDeletionInfo } from '../types';
import { changeRequestsApi } from '../services/apiService';
import { Ionicons } from '@expo/vector-icons';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
interface DeletionApprovalBannerProps {
  deletionInfo: PendingDeletionInfo;
  onApproved: () => void;
  onRejected: () => void;
  onViewImpact?: () => void;
  canApprove: boolean;
  canCancel: boolean;
  onCancelled?: () => void;
}

export default function DeletionApprovalBanner({
  deletionInfo,
  onApproved,
  onRejected,
  onViewImpact,
  canApprove,
  canCancel,
  onCancelled,
}: DeletionApprovalBannerProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      await changeRequestsApi.approve(deletionInfo.change_request_id);
      onApproved();
    } catch (err) {
      // Error handled by caller
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    setLoading(true);
    try {
      await changeRequestsApi.reject(deletionInfo.change_request_id, rejectReason);
      onRejected();
    } catch (err) {
      // Error handled by caller
    } finally {
      setLoading(false);
      setShowRejectInput(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await changeRequestsApi.cancel(deletionInfo.change_request_id);
      onCancelled?.();
    } catch (err) {
      // Error handled by caller
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = deletionInfo.requested_at
    ? new Date(deletionInfo.requested_at).toLocaleDateString(isRTL ? 'ar-AE' : 'en-US')
    : '';

  return (
    <View style={styles.banner}>
      <View style={[styles.row, isRTL && styles.rowRTL]}>
        <Ionicons name="warning" size={20} color={tokens.colors.raw.yellow["500"]} />
        <View style={styles.textContainer}>
          <Text style={[styles.title, isRTL && styles.rtlText]}>
            {t('deletion.pendingDeletion')}
          </Text>
          <Text style={[styles.subtitle, isRTL && styles.rtlText]}>
            {t('deletion.requestedBy', { name: deletionInfo.requested_by_name })}
            {' '}
            {t('deletion.requestedAt', { date: formattedDate })}
          </Text>
        </View>
      </View>

      {showRejectInput && (
        <TextInput
          style={[styles.rejectInput, isRTL && styles.rtlText]}
          placeholder={t('deletion.reason')}
          value={rejectReason}
          onChangeText={setRejectReason}
          multiline
        />
      )}

      <View style={[styles.actions, isRTL && styles.actionsRTL]}>
        {onViewImpact && (
          <Button variant="secondary" size="sm" onPress={onViewImpact}>
            {t('deletion.viewImpact')}
          </Button>
        )}

        {canCancel && (
          <Button variant="secondary" size="sm" onPress={handleCancel} disabled={loading}>
            {t('deletion.cancelRequest')}
          </Button>
        )}

        {canApprove && (
          <>
            <Button
              variant="destructive"
              size="sm"
              onPress={handleReject}
              loading={loading && showRejectInput}
              disabled={loading && !showRejectInput}
            >
              {t('deletion.rejectRequest')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onPress={handleApprove}
              loading={loading && !showRejectInput}
              disabled={loading && showRejectInput}
            >
              {t('deletion.approveRequest')}
            </Button>
          </>
        )}
      </View>
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  banner: {
    backgroundColor: '#FFFBEB',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#FDE68A',
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["3"],
  },
  rowRTL: {
    flexDirection: 'row-reverse',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 15,
    fontFamily: FONTS.semiBold,
    color: '#854D0E',
    marginBottom: tokens.spacing.absolute["0_5"],
  },
  subtitle: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: '#A16207',
  },
  actions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["3"],
    flexWrap: 'wrap',
  },
  actionsRTL: {
    flexDirection: 'row-reverse',
  },
  rejectInput: {
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#D4D4D4',
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["2_5"],
    marginTop: tokens.spacing.absolute["3"],
    minHeight: 60,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
  },
  rtlText: {
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);
